import * as React from 'react'

import { Button, HrefGamma, Icon } from '@factorialco/deprecated-design-system'
import { Features } from 'generated/featureDefinitions'
import { isNil } from 'lodash'

import { useFeatureAvailability } from 'modules/core/hooks/useFeatureAvailability'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import helpCenterUrl from 'modules/core/lib/helpCenterUrl'
import i18n from 'modules/core/lib/i18n'
import { TentacleErrorData, isTentacleErrorType } from 'modules/core/lib/parseErrorData'

import ReportBlankSlate from 'modules/reports/components/BlankSlate'
import { ChartError } from 'modules/reports/components/Chart/ChartError'
import { ContentError } from 'modules/reports/store/models/Content'

interface Props {
  error: TentacleErrorData | Error
}

const getErrorType = (error: TentacleErrorData | Error) => {
  if (error instanceof ContentError) {
    return error.error_type
  } else if (error instanceof ChartError) {
    return error.errorType
  } else if (isTentacleErrorType(error)) {
    return error.error
  }
}

/**
 * Error component to use inside a report.
 */
const ErrorBlankSlate = ({ error }: Props) => {
  const { featureAvailability } = useFeatureAvailability(Features.analytics_advanced_reports)
  const { navigation } = useNavigation()

  const errorType = getErrorType(error)

  let errorLabel = i18n.t('custom_reports.editor.generic_error')
  let description = i18n.t('custom_reports.editor.error_description')
  let help: string | undefined
  let helpUrl = helpCenterUrl('reports_errors')
  let icon: React.ComponentProps<typeof Icon.Large>['icon'] = 'alert'

  switch (errorType) {
    case 'CUSTOM_FIELD_NOT_FOUND': {
      errorLabel = i18n.t('custom_reports.editor.custom_field_conflict_error')
      break
    }
    case 'SERVER_ERROR': {
      description = error.message
      break
    }
    case 'SyntaxError': {
      errorLabel = i18n.t('custom_reports.errors.syntax.notice')
      description = i18n.t('custom_reports.errors.syntax.description')
      help = i18n.t('custom_reports.errors.syntax.help')
      const { reportId, isSQL } = error as ContentError
      if (isNil(reportId)) {
        help = undefined
        break
      }

      if (!featureAvailability.available) {
        helpUrl = navigation.reports.id.fullPath({ id: reportId })
        break
      }
      if (isSQL) {
        helpUrl = navigation.reports.id.sqlEditor.fullPath({ id: reportId })
      } else {
        helpUrl = navigation.reports.id.edit.fullPath({ id: reportId })
      }
      break
    }
    case 'MissingTable':
    case 'DeprecatedError': {
      errorLabel = i18n.t('custom_reports.errors.missing_table.notice')
      description = i18n.t('custom_reports.errors.missing_table.description')
      help = i18n.t('custom_reports.errors.missing_table.help')
      break
    }
    case 'ResultSizeLimitExceeded': {
      errorLabel = i18n.t('custom_reports.errors.result_size_limit_exceeded.notice')
      description = i18n.t('custom_reports.errors.result_size_limit_exceeded.description')
      icon = 'wireframe'
      break
    }
    case 'IncompleteReportConfiguration': {
      errorLabel = i18n.t('custom_reports.errors.incomplete_report_configuration.notice')
      description = i18n.t('custom_reports.errors.incomplete_report_configuration.description')
      break
    }
    case 'Unexpected': {
      errorLabel = i18n.t('custom_reports.errors.unexpected.notice')
      description = i18n.t('custom_reports.errors.unexpected.description')
      help = i18n.t('custom_reports.errors.unexpected.help')
      break
    }
  }

  return (
    <ReportBlankSlate message={errorLabel} description={description} illustration={icon}>
      {help && (
        <HrefGamma href={helpUrl} target="_blank" rel="nofollow noopener noreferrer">
          <Button type="secondary">{help}</Button>
        </HrefGamma>
      )}
    </ReportBlankSlate>
  )
}

export default ErrorBlankSlate
